import { useState, useEffect, useRef, useCallback } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import { Input } from '@/components/ui/input';
import { MapPin, Check, Loader2, AlertCircle } from 'lucide-react';

const libraries = ['places'];

// Extract address components from Google Place result
const extractAddressComponents = (place) => {
  const components = {
    street: '',
    city: '',
    state: '',
    zip: ''
  };
  
  let streetNumber = '';
  let streetName = '';
  
  if (place.address_components) {
    for (const component of place.address_components) {
      const types = component.types;
      
      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      }
      if (types.includes('route')) {
        streetName = component.long_name;
      }
      if (types.includes('locality')) {
        components.city = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        components.state = component.short_name;
      }
      if (types.includes('postal_code')) {
        components.zip = component.long_name;
      }
    }
  }
  
  components.street = streetNumber ? `${streetNumber} ${streetName}` : streetName;
  
  return components;
};

const AddressAutocomplete = ({ 
  onAddressSelect, 
  initialValue = '',
  placeholder = "Start typing an address...",
  className = '',
  error = '',
  testId = 'address-autocomplete'
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [isValidAddress, setIsValidAddress] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  
  const inputRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const dropdownRef = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries
  });

  // Initialize services when script is loaded
  useEffect(() => {
    if (isLoaded && window.google) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  }, [isLoaded]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch predictions from Google Places
  const fetchPredictions = useCallback((input) => {
    if (!autocompleteServiceRef.current || !input.trim()) {
      setPredictions([]);
      return;
    }

    setIsSearching(true);
    
    autocompleteServiceRef.current.getPlacePredictions(
      {
        input,
        componentRestrictions: { country: 'us' },
        types: ['address'],
        sessionToken: sessionTokenRef.current
      },
      (results, status) => {
        setIsSearching(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results);
          setShowDropdown(true);
        } else {
          setPredictions([]);
        }
      }
    );
  }, []);

  // Handle input change with debounce
  useEffect(() => {
    if (!hasInteracted) return;
    
    const debounceTimer = setTimeout(() => {
      if (inputValue.trim().length >= 3) {
        fetchPredictions(inputValue);
      } else {
        setPredictions([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [inputValue, fetchPredictions, hasInteracted]);

  // Handle prediction selection
  const handleSelect = (prediction) => {
    if (!placesServiceRef.current) return;
    
    // Disable the input change effect by setting hasInteracted to false temporarily
    setHasInteracted(false);
    
    // Close dropdown immediately when user clicks
    setShowDropdown(false);
    setPredictions([]);

    placesServiceRef.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['address_components', 'formatted_address', 'name', 'types'],
        sessionToken: sessionTokenRef.current
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const addressData = extractAddressComponents(place);
          
          // Update input with formatted address
          setInputValue(place.formatted_address || prediction.description);
          setIsValidAddress(true);
          
          // Create new session token for next search
          sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
          
          // Check if this is a residential address
          const residentialTypes = ['premise', 'street_address', 'subpremise', 'route'];
          const isResidence = place.types?.some(type => residentialTypes.includes(type)) && 
                             !place.name?.match(/\d/) === false; // Has numbers in name = likely just address
          
          // Determine location name: use place name for businesses, "Private Residence" for homes
          let locationName = null;
          if (place.name && place.name !== place.formatted_address && 
              !place.name.match(/^\d+\s/)) { // Doesn't start with a number (like "123 Main St")
            locationName = place.name;
          } else if (isResidence || !place.name || place.name === place.formatted_address) {
            locationName = "Private Residence";
          }
          
          // Callback with address data, formatted address, and location name
          onAddressSelect(addressData, place.formatted_address, locationName);
        } else {
          // On error, still use the prediction description
          setInputValue(prediction.description);
          console.error('Places API error:', status);
        }
      }
    );
  };

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setIsValidAddress(false);
    setHasInteracted(true);
    
    // Clear the address if user is typing (not a valid selected address)
    if (hasInteracted) {
      onAddressSelect(null, '');
    }
  };

  // Handle input focus
  const handleFocus = () => {
    setHasInteracted(true);
    if (predictions.length > 0) {
      setShowDropdown(true);
    }
  };

  if (loadError) {
    return (
      <div className="text-red-400 text-sm flex items-center gap-2">
        <AlertCircle className="h-4 w-4" />
        Failed to load Google Maps
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={!isLoaded ? "Loading..." : placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          disabled={!isLoaded}
          autoComplete="off"
          className={`pl-10 pr-10 ${className} ${error ? 'border-red-500' : ''} ${isValidAddress ? 'border-green-500' : ''}`}
          data-testid={testId}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {isSearching && <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />}
          {!isSearching && isValidAddress && <Check className="h-4 w-4 text-green-500" />}
        </div>
      </div>
      
      {/* Predictions dropdown */}
      {showDropdown && predictions.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl overflow-hidden"
        >
          {predictions.map((prediction) => (
            <button
              key={prediction.place_id}
              type="button"
              onClick={() => handleSelect(prediction)}
              className="w-full px-4 py-3 text-left text-sm text-slate-200 hover:bg-slate-700 focus:bg-slate-700 focus:outline-none border-b border-slate-700 last:border-b-0 flex items-start gap-3"
              data-testid={`address-suggestion-${prediction.place_id}`}
            >
              <MapPin className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium">{prediction.structured_formatting?.main_text}</div>
                <div className="text-xs text-slate-400">{prediction.structured_formatting?.secondary_text}</div>
              </div>
            </button>
          ))}
        </div>
      )}
      
      {/* Validation message */}
      {hasInteracted && !isValidAddress && inputValue.trim() && !showDropdown && !isSearching && (
        <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          Please select an address from the suggestions
        </p>
      )}
      
      {error && (
        <p className="text-xs text-red-400 mt-1">{error}</p>
      )}
    </div>
  );
};

export default AddressAutocomplete;
